import { useEffect, useState } from 'react'
import { PageHeader } from '../../components/PageHeader'
import { useAuth } from '../../lib/auth'
import { useI18n, LANGS } from '../../i18n'
import { readError } from '../../lib/errors'

interface Props {
  onBack: () => void
}

/**
 * Language picker (owner spec 2026-08-11: a "Language" row under
 * Notifications that opens a list). Native names on purpose — a
 * Japanese speaker lost in an English UI must be able to find 日本語.
 *
 * The "Play with other languages" toggle only shapes online
 * quick-match for the non-weekly games; weekly events stay global.
 * Player-written content is never translated either way.
 */
export function LanguageScreen({ onBack }: Props) {
  const { profile, updateProfile } = useAuth()
  const { lang, setLang, t } = useI18n()
  const [allLangs, setAllLangs] = useState<boolean>(!!profile?.play_all_languages)
  const [error, setError] = useState<string | null>(null)

  // Keep the toggle in step with the profile. Without this the state is
  // frozen at whatever the profile held on first mount, which is `false`
  // when the screen opens before the profile has loaded.
  useEffect(() => {
    setAllLangs(!!profile?.play_all_languages)
  }, [profile?.play_all_languages])

  async function toggleAllLanguages() {
    if (!profile?.id) return
    const next = !allLangs
    setAllLangs(next)
    setError(null)
    try {
      // Through the context, not a raw query: this also refreshes the
      // cached profile, which is what makes the choice survive leaving
      // and coming back. A bare update writes the row and leaves the app
      // holding the old value.
      await updateProfile({ play_all_languages: next })
    } catch (e) { setAllLangs(!next); setError(readError(e)) }
  }

  return (
    <div className="screen pb-12">
      <PageHeader title={t('Language')} onBack={onBack} />

      <div className="px-5 py-5 space-y-6 overflow-y-auto">
        <p className="text-sm text-brand-muted">
          {t('Choose the language the app uses. Stories and submissions from other players stay in the language they were written in.')}
        </p>

        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        <div className="space-y-2">
          {LANGS.map(l => (
            <button
              key={l.code}
              type="button"
              onClick={() => setLang(l.code)}
              className="card w-full p-4 flex items-center gap-3 text-left
                         active:bg-brand-elevated transition-colors"
            >
              <span className="flex-1 font-medium text-brand-text">{l.label}</span>
              {lang === l.code && (
                <svg width="20" height="20" viewBox="0 0 24 24" className="shrink-0">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
                        fill="#B8C01F" />
                </svg>
              )}
            </button>
          ))}
        </div>

        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Online play')}
          </p>
          <label className="card p-4 flex items-start gap-3 cursor-pointer
                            active:bg-brand-elevated transition-colors">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text">{t('Play with other languages')}</p>
              <p className="text-xs text-brand-muted mt-0.5">
                {t('Quick-match can pair you with players of any language, not only yours. Weekly games are always worldwide.')}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allLangs}
              onClick={toggleAllLanguages}
              className={`relative w-12 h-7 rounded-full transition-colors shrink-0
                          ${allLangs ? 'bg-brand-lime-dark' : 'bg-brand-border'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-xs
                                transition-transform ${allLangs ? 'translate-x-5' : ''}`} />
            </button>
          </label>
        </div>
      </div>
    </div>
  )
}
